import { useMemo, useState } from 'react';
import debounce from 'lodash.debounce';

import { useCapabilityValue } from '../../../store/devices/useCapabilityValue';
import { useDevice } from '../../../store/devices/useDevices';

import { ThermostatRadial } from './ThermostatRadial';
import { CapabilitySelect } from '../CapabilitySelect';

import * as Containers from '../Containers';

export function Thermostat(props) {
  const { device } = useDevice({ deviceId: props.deviceId });

  const componentCapabilities = useMemo(() => {
    const mapping = {};

    for (const capabilityId of props.component.capabilities) {
      const isTargetTemperature = capabilityId.startsWith('target_temperature');
      const [, subcapabilityId] = capabilityId.split('.');

      if (mapping[subcapabilityId] == null) {
        mapping[subcapabilityId] = capabilityId;
      }

      // prefer target temperature
      if (isTargetTemperature && mapping[subcapabilityId] != null) {
        mapping[subcapabilityId] = capabilityId;
      }
    }

    return Object.values(mapping);
  }, [props.component.capabilities]);

  const [selectedCapabilityId, setSelectedCapabilityId] = useState(() => {
    return componentCapabilities[0];
  });

  let keys = [];
  if (selectedCapabilityId != null) {
    const [, subcapabilityId] = selectedCapabilityId.split('.');

    if (subcapabilityId === undefined) {
      keys = ['target_temperature', 'measure_temperature', 'thermostat_mode'];
    } else {
      keys = [
        `target_temperature.${subcapabilityId}`,
        `measure_temperature.${subcapabilityId}`,
        `thermostat_mode.${subcapabilityId}`,
      ];
    }
  }

  const target_temperature = device?.capabilitiesObj?.[keys[0]] ?? null;
  const measure_temperature = device?.capabilitiesObj?.[keys[1]] ?? null;
  const thermostat_mode = device?.capabilitiesObj?.[keys[2]] ?? null;

  const [targetTemperatureValue, setTargetTemperatureValueValue, targetTemperatureCapabilityObj] =
    useCapabilityValue({
      deviceId: props.deviceId,
      capabilityId: target_temperature?.id ?? null,
    });
  const [measureTemperatureValue, , measureTemperatureCapabilityObj] = useCapabilityValue({
    deviceId: props.deviceId,
    capabilityId: measure_temperature?.id ?? null,
  });
  const [thermostatModeValue, ,] = useCapabilityValue({
    deviceId: props.deviceId,
    capabilityId: thermostat_mode?.id ?? null,
  });

  const onTargetValueChange = useMemo(() => {
    return debounce(function (value) {
      setTargetTemperatureValueValue(value)
        .then(() => {})
        .catch((error) => {});
    }, 1000);
  }, [setTargetTemperatureValueValue]);

  return (
    <Containers.Control>
      <Containers.Select>
        {componentCapabilities.length > 1 && (
          <CapabilitySelect
            selectedCapabilityId={selectedCapabilityId}
            componentCapabilities={componentCapabilities}
            capabilitiesObj={device?.capabilitiesObj}
            onSelectionChange={setSelectedCapabilityId}
          />
        )}
      </Containers.Select>
      <Containers.Action>
        <ThermostatRadial
          min={targetTemperatureCapabilityObj?.min ?? 0}
          max={targetTemperatureCapabilityObj?.max ?? 40}
          step={targetTemperatureCapabilityObj?.step ?? 0.5}
          units={targetTemperatureCapabilityObj?.units ?? null}
          targetValue={targetTemperatureValue}
          targetValueDecimals={targetTemperatureCapabilityObj?.decimals}
          measureValue={measureTemperatureValue}
          measureValueDecimals={measureTemperatureCapabilityObj?.decimals}
          hideMeasureTemperature={measure_temperature == null}
          modeValue={thermostatModeValue}
          onTargetValueChange={onTargetValueChange}
        />
      </Containers.Action>
    </Containers.Control>
  );
}
